import styled from '@emotion/styled'
import Typography from '@mui/material/Typography'
import { useEffect } from 'react'
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { useTypedDispatch, useTypedSelector } from '..'
import { actAsToolUser } from '../actions/api'
import { DropdownItem } from '../components/act-as-tool/types'
import Button from '../components/button/Button'
import { FormInputAutocomplete } from '../components/create-defect/input/FormInputAutocomplete'
import { FormInputCheckbox } from '../components/create-defect/input/FormInputCheckbox'
import { FormInputText } from '../components/create-defect/input/FormInputText'
import { FormInputLabel } from '../components/create-defect/input/InputLabel'
import Page from '../components/page/Page'
import { Container } from '../components/page/PageComponents'
import SearchPageHeader from '../components/search/SearchPageHeader'
import { adminRoleKey, properNonAdminRoleKeys, readAdminRoleKey } from '../lib/roleUtils'
import { color, MaybeColor } from '../Theme'
import type { UserState } from '../types'

const PaddedContainer = styled.div`
  margin-left: 20px;
  margin-right: 20px;
`

interface ActionButtonProps {
  width?: string
  color?: MaybeColor
  disabled?: boolean
  marginBottom?: string
  light?: boolean
  smallText?: boolean
}

const ActionButton = styled(Button)<ActionButtonProps>`
  width: ${(p) => p.width ?? '50%'};
  height: 56px;
  margin-bottom: ${(p) => p.marginBottom ?? '0'};
  cursor: ${(p) => (p.disabled ? 'default' : 'pointer')};
  background: ${(p) => {
    if (p.light) return 'rgba(36, 121, 179, 0.5)'
    if (p.disabled) return color('grayDark')
    if (p.color) return color(p.color)
    return color('primaryBlue')
  }};
  text-transform: ${(p) => (p.smallText ? 'none' : 'uppercase')};
`

const FullRowSelectionContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-top: 20px;
`

const DualSelectionRowContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 20px;
  margin-top: 20px;
`

const ActAsToolPage = () => {
  const { t } = useTranslation()
  const dispatch = useTypedDispatch()
  const user = useTypedSelector((state) => state.user)

  type FormValues = {
    mainRole: string
    additionalReadAdminRole: boolean
    personnelNumber: string
  }

  const formMethods = useForm<FormValues>({
    defaultValues: {
      mainRole: '',
      additionalReadAdminRole: false,
      personnelNumber: '',
    },
    mode: 'onTouched',
  })

  const mainRole = formMethods.watch('mainRole')
  //const additionalReadAdminRole = formMethods.watch('additionalReadAdminRole')

  useEffect(() => {
    const userRoles = Object.keys(user).filter(
      (key) => user[key as keyof UserState] === true && key !== readAdminRoleKey
    )
    const hasReadAdmin = user.read_admin

    if (hasReadAdmin) {
      if (userRoles.length > 0) {
        formMethods.setValue('mainRole', userRoles[0])
        formMethods.setValue('additionalReadAdminRole', true)
      } else {
        formMethods.setValue('mainRole', readAdminRoleKey)
      }
    } else if (userRoles.length > 0) {
      formMethods.setValue('mainRole', userRoles[0])
    }

    if (user.number && user.originalNumber && user.number !== user.originalNumber) {
      formMethods.setValue('personnelNumber', user.number)
    }
  }, [user, formMethods])

  const onSubmit: SubmitHandler<FormValues> = (values) => {
    dispatch(
      actAsToolUser(
        values.personnelNumber || undefined,
        values.mainRole || undefined,
        values.additionalReadAdminRole,
        user
      )
    ).then(() => {
      // brute-force page reload to reset all state
      window.location.href = '/'
    })
  }

  const createRoleDropdownOption = (roleId: string): DropdownItem => ({
    value: roleId,
    label: `${roleId} (${t('actAsTool.' + roleId)})`,
  })

  const roleUndefined = 'undefined'

  return (
    <Page overflowVisible={true}>
      <Container>
        <SearchPageHeader name="actAsTool" text={t('actAsTool.title')} />
        <PaddedContainer>
          <FormProvider {...formMethods}>
            <form onSubmit={formMethods.handleSubmit(onSubmit)}>
              <FullRowSelectionContainer>
                <FormInputLabel
                  label={t('actAsTool.roleHint')}
                  htmlFor="mainRole"
                  sx={{ paddingLeft: 0 }}
                />
                <FormInputAutocomplete
                  name="mainRole"
                  label={t('actAsTool.role')}
                  options={[
                    adminRoleKey,
                    readAdminRoleKey,
                    ...properNonAdminRoleKeys,
                    roleUndefined,
                  ].map((o) => createRoleDropdownOption(o))}
                  status="succeeded"
                />
                <FormInputCheckbox
                  name="additionalReadAdminRole"
                  label={t('actAsTool.additionalReadAdminRole')}
                  sx={{
                    marginLeft: 0, // remove extra indent
                    '.MuiFormControlLabel-label': {
                      fontSize: '0.95rem',
                      color: 'text.primary',
                    },
                  }}
                />
              </FullRowSelectionContainer>
              <DualSelectionRowContainer>
                <FormInputText name="personnelNumber" label={t('actAsTool.personnelNumber')} />
                <ActionButton type="submit" disabled={!mainRole}>
                  {t('actAsTool.submit')}
                </ActionButton>
              </DualSelectionRowContainer>
              <Typography variant="body2" color="text.secondary" sx={{ marginTop: '8px' }}>
                {t('actAsTool.personnelNumberHint')}
              </Typography>
            </form>
          </FormProvider>
        </PaddedContainer>
      </Container>
    </Page>
  )
}

export default ActAsToolPage
